import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDebounce} from '@github-ui/use-debounce'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

interface AccessibilityCheckResponse {
  inaccessible: string[]
}

const USER_ATTACHMENT_GUID_RE =
  /user-attachments\/assets\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi
const DEBOUNCE_MS = 500

/**
 * Hook that reactively checks for inaccessible attachments as the user
 * types or pastes content containing attachment GUIDs.
 *
 * A debounced effect fires when `value` changes. If inaccessible
 * attachments are found, `inaccessibleCount` is set and an inline
 * banner can be shown. The banner clears automatically when the
 * problematic attachments are removed from the value.
 */
export function useAttachmentAccessibilityCheck({
  repositoryNwo,
  value,
}: {
  repositoryNwo: string | undefined
  value: string
}) {
  const [inaccessibleCount, setInaccessibleCount] = useState(0)
  const isCheckingRef = useRef(false)
  const lastCheckedKeyRef = useRef('')

  const runCheck = useCallback(async (nwo: string, guids: string[], key: string) => {
    if (isCheckingRef.current) return

    isCheckingRef.current = true
    try {
      const searchParams = new URLSearchParams()
      for (const guid of guids) {
        searchParams.append('guids[]', guid)
      }

      const response = await verifiedFetchJSON(`/${nwo}/attachment_accessibility_check?${searchParams.toString()}`)

      if (response.ok) {
        const data = (await response.json()) as AccessibilityCheckResponse
        lastCheckedKeyRef.current = key
        setInaccessibleCount(data.inaccessible.length)
      }
    } catch {
      // If the check fails, don't show a warning
    } finally {
      isCheckingRef.current = false
    }
  }, [])

  const debouncedCheck = useDebounce(
    (nwo: string, guids: string[], key: string) => {
      void runCheck(nwo, guids, key)
    },
    DEBOUNCE_MS,
    {onChangeBehavior: 'cancel'},
  )

  const guids = useMemo(
    () => [
      ...new Set([...value.matchAll(USER_ATTACHMENT_GUID_RE)].map(m => m[1]).filter((g): g is string => g != null)),
    ],
    [value],
  )

  const hasGuids = guids.length > 0

  useEffect(() => {
    if (!isFeatureEnabled('warn_inaccessible_attachments') || !repositoryNwo || !hasGuids) {
      return
    }

    // Don't re-check if the GUID set + repo hasn't changed
    const checkKey = `${repositoryNwo}:${[...guids].sort().join(',')}`
    if (checkKey === lastCheckedKeyRef.current) {
      return
    }

    debouncedCheck(repositoryNwo, guids, checkKey)
  }, [hasGuids, guids, repositoryNwo, debouncedCheck])

  const dismissWarning = useCallback(() => {
    setInaccessibleCount(0)
  }, [])

  // Reset count when all attachment GUIDs are removed from the value
  const effectiveCount = hasGuids ? inaccessibleCount : 0

  return {
    inaccessibleCount: effectiveCount,
    dismissWarning,
  }
}
